import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { MapPin, Clock, Star, DollarSign, Check, Upload, X, Camera } from "lucide-react";
import RatingDialog from "@/components/RatingDialog";

export default function TaskDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ task: null, applications: [] });
  const [msg, setMsg] = useState("");
  const [price, setPrice] = useState("");
  const [rateOpen, setRateOpen] = useState(false);
  const [completionPhoto, setCompletionPhoto] = useState("");
  const [uploadingCompletion, setUploadingCompletion] = useState(false);

  const load = () => api.get(`/tasks/${id}`).then(({ data }) => setState(data));
  useEffect(() => { load(); }, [id]);

  const task = state.task;
  if (!task) return <div className="text-slate-500">Loading…</div>;

  const isOwner = user?.user_id === task.owner_id;
  const isLaborer = user?.role === "laborer";
  const myApp = state.applications.find(a => a.laborer_id === user?.user_id);

  const apply = async () => {
    try {
      await api.post("/applications", { task_id: id, message: msg, proposed_price: price ? parseFloat(price) : null });
      toast.success("Application sent!");
      setMsg(""); setPrice(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const accept = async (appId) => {
    try { await api.post(`/applications/${appId}/accept`); toast.success("Applicant hired!"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markComplete = async () => {
    try {
      await api.post(`/tasks/${id}/complete`, { completion_photo_url: completionPhoto || null });
      toast.success("Marked as complete");
      setCompletionPhoto("");
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadCompletionPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCompletion(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.file_id}`;
      setCompletionPhoto(url);
      toast.success("Proof photo attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingCompletion(false); }
  };

  const pay = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { task_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const statusStyles = {
    open: "bg-blue-50 text-blue-700 border-blue-200",
    assigned: "bg-amber-50 text-amber-700 border-amber-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-50">{task.category}</Badge>
            <Badge className={statusStyles[task.status] + " capitalize"}>{task.status}</Badge>
            {task.payment_status === "paid" && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Paid</Badge>}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">{task.title}</h1>
          <div className="mt-2 text-sm text-slate-600 flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1"><MapPin size={14} /> {task.location}</span>
            {task.scheduled_date && <span className="inline-flex items-center gap-1"><Clock size={14} /> {task.scheduled_date}</span>}
            <span>Posted by {task.owner_name}</span>
          </div>
        </div>
        <div className="font-mono-money text-lg px-4 py-2 rounded-2xl bg-[#3E2A4E] text-[#D9FF3D]">${task.budget}</div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl p-6">
        {task.photo_url && (
          <img src={task.photo_url} alt="Task" className="w-full max-h-80 object-cover rounded-xl mb-4 border border-slate-200" data-testid="task-photo" />
        )}
        <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{task.description}</p>
        {task.skills_required?.length ? (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {task.skills_required.map((s) => <Badge key={s} variant="outline" className="rounded-full">{s}</Badge>)}
          </div>
        ) : null}
        {task.completion_photo_url && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Camera size={12} /> Completion proof
            </div>
            <img src={task.completion_photo_url} alt="Completion" className="w-full max-h-80 object-cover rounded-xl border border-emerald-200" data-testid="completion-photo" />
          </div>
        )}
      </div>

      {/* Actions */}
      {isOwner && task.status === "assigned" && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="font-heading font-bold">Assigned to {task.assigned_laborer_name}</div>
            <div className="text-sm text-slate-600">When the job's done, mark complete and pay.</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={markComplete} data-testid="mark-complete-btn" className="flex-1 sm:flex-none">Mark complete</Button>
          </div>
        </div>
      )}
      {isOwner && task.status === "completed" && task.payment_status !== "paid" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="font-heading font-bold">Ready to pay</div>
            <div className="text-sm text-slate-600">Includes a 1% platform fee — pay securely via Stripe.</div>
          </div>
          <Button onClick={pay} data-testid="pay-now-btn" className="bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold rounded-full px-6 btn-primary-glow w-full sm:w-auto justify-center">
            <DollarSign size={16} className="mr-1" /> Pay ${(task.budget * 1.01).toFixed(2)}
          </Button>
        </div>
      )}
      {isOwner && task.status === "paid" && (
        <div className="bg-slate-900 text-white rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="font-heading font-bold flex items-center gap-2"><Check size={18} className="text-emerald-400" /> Payment sent</div>
            <div className="text-sm text-white/70">Leave a rating for {task.assigned_laborer_name}.</div>
          </div>
          <Button onClick={() => setRateOpen(true)} data-testid="open-rating-btn" className="bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-full px-6 font-bold w-full sm:w-auto justify-center">
            <Star size={16} className="mr-1" /> Rate helper
          </Button>
        </div>
      )}

      {/* Applications */}
      {isOwner && task.status === "open" && (
        <div>
          <h2 className="font-heading text-xl font-bold mb-3">Applications ({state.applications.length})</h2>
          {state.applications.length === 0 ? (
            <div className="text-slate-500 text-sm">No applications yet.</div>
          ) : (
            <div className="space-y-3">
              {state.applications.map((a) => (
                <div key={a.application_id} className="bg-white border border-slate-200/80 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <Link to={`/helpers/${a.laborer_id}`} className="font-heading font-bold hover:text-[#3E2A4E]">
                      {a.laborer_name} {a.laborer?.avg_rating > 0 && <span className="text-xs text-amber-500 ml-2"><Star size={12} className="inline -mt-0.5" /> {a.laborer.avg_rating}</span>}
                    </Link>
                    {a.message && <div className="text-sm text-slate-600 mt-1">{a.message}</div>}
                    {a.proposed_price && <div className="text-xs font-mono-money text-slate-900 mt-1">Proposed: ${a.proposed_price}</div>}
                  </div>
                  <Button size="sm" onClick={() => accept(a.application_id)} data-testid={`accept-app-${a.application_id}`} className="bg-[#3E2A4E] hover:bg-[#2E1F3B] text-[#F3EFFA] rounded-full">
                    Hire
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Helper apply / status */}
      {isLaborer && task.status === "open" && !myApp && (
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 space-y-3">
          <h2 className="font-heading font-bold">Apply for this task</h2>
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Introduce yourself — why you're a good fit." rows={3} data-testid="apply-message-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input value={price} onChange={(e) => setPrice(e.target.value)} type="number" placeholder="Optional counter-offer $" data-testid="apply-price-input" className="h-11" />
            <Button onClick={apply} data-testid="apply-submit-btn" className="h-11 bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold rounded-full btn-primary-glow">Send application</Button>
          </div>
        </div>
      )}
      {isLaborer && myApp && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm">
          Your application is <b>{myApp.status}</b>.
        </div>
      )}
      {isLaborer && task.status === "assigned" && task.assigned_laborer_id === user.user_id && (
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 space-y-3">
          <h2 className="font-heading font-bold">Mark job complete</h2>
          <p className="text-sm text-slate-600">Attach an after-photo — it helps the client feel safe paying you.</p>
          {completionPhoto ? (
            <div className="relative w-full max-w-sm">
              <img src={completionPhoto} alt="Proof" className="w-full h-40 object-cover rounded-xl border border-slate-200" />
              <button type="button" onClick={() => setCompletionPhoto("")} className="absolute top-2 right-2 bg-white/95 rounded-full p-1.5 shadow" data-testid="remove-completion-photo-btn">
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-300 hover:bg-slate-50 cursor-pointer text-sm font-semibold">
              <Upload size={14} /> {uploadingCompletion ? "Uploading…" : "Attach proof photo"}
              <input type="file" accept="image/*" onChange={uploadCompletionPhoto} className="hidden" data-testid="completion-photo-input" />
            </label>
          )}
          <Button onClick={markComplete} data-testid="helper-mark-complete-btn" className="bg-[#3E2A4E] hover:bg-[#2E1F3B] text-[#F3EFFA] rounded-full">
            Mark job complete
          </Button>
        </div>
      )}

      <RatingDialog
        open={rateOpen}
        onOpenChange={setRateOpen}
        taskId={task.task_id}
        laborerId={task.assigned_laborer_id}
        onDone={load}
      />
    </div>
  );
}
